/**
 * Identity-type constants shared by the patient + guardian identity blocks.
 *
 * **Why a GUID and not a name match** (PROD-1756):
 * React used to decide "is this an Emirates ID?" by string-matching the
 * identity-type NAME — `idTypeName.toLowerCase().includes('emirates')`. But
 * `GET /api/Master/IdentityType` only ever returns Driving License /
 * National ID / Passport — no name contains "emirates", so that branch was
 * DEAD: the `784-YYYY-NNNNNNN-N` input mask and the 15-digit cap never
 * applied, and React persisted raw unformatted digits while Flutter stored
 * the formatted `784-1991-2223334-2`. A real wire-level divergence on
 * patient identity data.
 *
 * Flutter keys off the idType GUID instead — the UAE National ID (which IS
 * the Emirates ID) has the fixed idType id below
 * (`identity_child_widget.dart:299`).
 *
 * Per the repo CLAUDE.md rule — "Magic strings from Flutter constants must
 * be copied verbatim — never inferred from English labels" — this GUID is
 * copied verbatim from Flutter and lives here as a single shared constant so
 * it is never inlined twice.
 */

/**
 * The `idType` GUID for the UAE National ID (Emirates ID) as returned by
 * `GET /api/Master/IdentityType`. Copied verbatim from Flutter
 * `identity_child_widget.dart:299`.
 */
export const NATIONAL_ID_TYPE_ID = 'f70b1710-f170-ea11-9192-0cc47a65f3a3';

/**
 * True when the selected identity-type id is the UAE National ID (Emirates
 * ID). This is the single source of the "should we apply the Emirates-ID
 * mask + 15-digit cap?" decision, used by both the patient and guardian
 * identity blocks.
 *
 * GUIDs may come back in varying case, so the compare is case-insensitive.
 */
export function isEmiratesIdType(idType: string | null | undefined): boolean {
  return (idType ?? '').toLowerCase() === NATIONAL_ID_TYPE_ID.toLowerCase();
}
